import { observer } from "mobx-react-lite";
import { type FC, useEffect } from "react";
import { type Control, useWatch } from "react-hook-form";

import { CustomCheckbox, CustomLabel, Tags } from "@/components/atoms";
import { CustomTooltip } from "@/components/atoms";
import { InputControl, toastWarning } from "@/components/molecules";
import type { Option } from "@/components/molecules/SelectOptions/BaseSelectOptions";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { TooltipProvider } from "@/components/ui/tooltip";

import type { DishFormType } from "../../BrandFormMenu/types";

const NutritionFields: { label: string; name: keyof DishFormType }[] = [
  { label: "Protein (g)", name: "protein" },
  { label: "Fiber (g)", name: "fiber" },
  { label: "Total fat (g)", name: "totalFat" },
  { label: "Total sugars (g)", name: "totalSugar" },
  { label: "Saturated fat (g)", name: "saturatedFat" },
  { label: "Added sugars (g)", name: "addedSugars" },
  { label: "Trans fat (g)", name: "transFat" },
  { label: "Salt (g)", name: "salt" },
  { label: "Cholesterol (mg)", name: "cholesterol" },
  { label: "Sodium (mg)", name: "sodium" },
  { label: "Carbohydrate (g)", name: "carbohydrate" },
  { label: "Caffeine (mg)", name: "caffeine" },
];

interface Props {
  control: Control<DishFormType>;
  diets: Option[];
  allergies: Option[];
  disabled?: boolean;
}

const StepDietNutrition: FC<Props> = observer(
  ({ control, diets, allergies, disabled }) => {
    const [totalFat, saturatedFat] = useWatch({
      control,
      name: ["totalFat", "saturatedFat"],
    });

    const [totalSugar, addedSugars] = useWatch({
      control,
      name: ["totalSugar", "addedSugars"],
    });

    useEffect(() => {
      if (Number(saturatedFat) > Number(totalFat)) {
        toastWarning(
          "Nutrient values are inconsistent (Saturated fat should not exceed total fat)."
        );
      }
    }, [totalFat, saturatedFat]);

    useEffect(() => {
      if (Number(addedSugars) > Number(totalSugar)) {
        toastWarning(
          "Nutrient values are inconsistent (Added sugars should not exceed total sugars)."
        );
      }
    }, [totalSugar, addedSugars]);

    return (
      <div className="flex flex-col gap-8">
        <div>
          <h2 className="text-xl font-bold mb-5">Diet & allergies</h2>
          <div className="flex flex-col gap-5">
            <Tags
              tagsList={diets}
              title="Diet"
              form={control}
              name="dietTags"
              optional
              disabled={disabled}
            />
            <Tags
              tagsList={allergies}
              title="Allergens"
              form={control}
              name="allergenTags"
              optional
              disabled={disabled}
            />
          </div>
        </div>

        <div>
          <h2 className="text-xl font-bold mb-5">Nutrition</h2>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <InputControl<DishFormType>
              formControl={control}
              label="Calories (kCal)"
              nameField="calories"
              placeholder="0"
              data-testid="dish-drawer-calories"
              disabled={disabled}
            />
            <InputControl<DishFormType>
              formControl={control}
              label="Walking minutes to burn"
              nameField="burnCaloriesTime"
              placeholder="0"
              data-testid="dish-drawer-burn-time"
              disabled={disabled}
            />
          </div>

          <TooltipProvider>
            <ul className="grid grid-cols-2 gap-x-8 gap-y-5 mb-5">
              {NutritionFields.map(elem => (
                <li
                  key={elem.name}
                  className="grid grid-cols-3 items-center gap-2"
                >
                  <div className="flex items-center gap-1 col-span-1">
                    <CustomLabel
                      htmlFor={String(elem.name)}
                      label={elem.label}
                      className="text-sm"
                    />
                    <CustomTooltip>
                      Nutritional information for {elem.label}.
                    </CustomTooltip>
                  </div>
                  <InputControl<DishFormType>
                    formControl={control}
                    nameField={elem.name}
                    placeholder="0"
                    classedName="mb-0 col-span-2"
                    data-testid={`dish-drawer-nutrition-${elem.name}`}
                    disabled={disabled}
                  />
                </li>
              ))}
            </ul>
          </TooltipProvider>

          <FormField
            control={control}
            name="saltContent"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2 text-sm text-gray-950">
                <FormControl>
                  <CustomCheckbox
                    name="saltContent"
                    id="saltContent"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="border-gray-950"
                    data-testid="dish-drawer-salt-check"
                    disabled={disabled}
                  />
                </FormControl>
                <CustomLabel htmlFor="saltContent" label="High salt content" />
              </FormItem>
            )}
          />
        </div>
      </div>
    );
  }
);

export default StepDietNutrition;
